<template>
  <!-- Public Contact Page (unauthenticated) -->
  <auth-split-layout
    v-if="!isAuthenticated"
    container-class="max-w-md"
    :nav-exclude="['contact']"
    show-login-link
  >
    <template #brand>
      <auth-brand-panel
        :eyebrow="t('auth.marketing.contact.eyebrow')"
        :title="t('auth.marketing.contact.title')"
        :subtitle="t('auth.marketing.contact.subtitle')"
        :body="t('auth.marketing.contact.body')"
        :features="brandContactFeatures"
      />
    </template>

    <template #top-link>
      <router-link
        to="/login"
        class="text-sm font-medium text-theme-text-secondary transition-colors hover:text-theme-palette-emerald"
      >
        {{ t('menu_login') }}
      </router-link>
    </template>

    <!-- Premium success state: once the message is sent the whole form is
         replaced by a confirmation + a forward CTA to the subscriptions page. -->
    <div v-if="submitted" class="text-center" data-test="contact-success">
      <div
        class="mx-auto mb-6 flex h-16 w-16 items-center justify-center rounded-full bg-theme-palette-emerald/10"
      >
        <icon :path="mdiCheckCircle" size="32" class="text-theme-palette-emerald" />
      </div>
      <h2 class="mb-2 text-2xl font-bold text-theme-text">
        {{ t('contact_success_title') }}
      </h2>
      <p class="mx-auto max-w-sm text-sm leading-relaxed text-theme-text-secondary">
        {{ t('contact_success_body') }}
      </p>
      <app-button
        class="mt-6"
        :label="t('contact_success_cta')"
        :icon="mdiArrowRight"
        to="/subscriptions"
        variant="solid"
        tone="primary"
        block
      />
      <router-link
        to="/"
        class="mt-4 inline-block text-sm text-theme-text-secondary transition-colors hover:text-theme-text-secondary"
      >
        {{ t('contact_success_back') }}
      </router-link>
    </div>

    <template v-else>
      <auth-form-header
        class="mb-6"
        :icon="mdiEmailOutline"
        :title="t('menu_contact')"
        :subtitle="t('general_question_help')"
      />

      <!-- Direct contact methods — only on small screens, where the dark brand
           panel (which already lists them) is hidden. Avoids the desktop dup. -->
      <div class="mb-6 flex flex-col gap-2 lg:hidden">
        <a
          href="mailto:info@profiteia.io"
          class="flex items-center gap-3 rounded-theme-md border border-theme-border bg-theme-surface px-4 py-2.5 text-sm text-theme-text-secondary transition-colors hover:border-theme-palette-emerald/40 hover:text-theme-palette-emerald"
        >
          <icon :path="mdiEmailOutline" class="h-5 w-5 text-theme-palette-emerald" />
          info@profiteia.io
        </a>
        <a
          href="tel:+31854884348"
          class="flex items-center gap-3 rounded-theme-md border border-theme-border bg-theme-surface px-4 py-2.5 text-sm text-theme-text-secondary transition-colors hover:border-theme-palette-emerald/40 hover:text-theme-palette-emerald"
        >
          <icon :path="mdiPhone" class="h-5 w-5 text-theme-palette-emerald" />
          085-4884348
        </a>
        <div
          class="flex items-center gap-3 rounded-theme-md border border-theme-border bg-theme-surface px-4 py-2.5 text-sm text-theme-text-secondary"
        >
          <icon :path="mdiMapMarkerOutline" class="h-5 w-5 text-theme-palette-emerald" />
          Spoorlaan 350, 5038 CC, Tilburg
        </div>
      </div>

      <!-- Contact Form -->
      <public-form
        :submit-label="t('general_send')"
        :submit-icon="mdiSendOutline"
        :submit-loading="isSubmitting"
        :submit-disabled="isSubmitDisabled"
        @submit="submitContactForm"
      >
        <app-alert
          v-if="errorMessage"
          tone="danger"
          :icon="mdiAlertCircle"
          variant="outline"
        >
          <b>{{ t('messages.error_title') }}:</b> {{ errorMessage }}
        </app-alert>

        <!-- Subject -->
        <app-input
          :label="t('general_subject')"
          v-model="contactForm.contactSubject"
          name="subject"
          :placeholder="t('general_subject')"
          container-class="mb-0"
          required
        />

        <!-- Name -->
        <app-input
          :label="t('general_name')"
          v-model="contactForm.contactFromName"
          name="name"
          autocomplete="name"
          :placeholder="t('general_name_help_required')"
          container-class="mb-0"
          required
        />

        <!-- Email -->
        <app-input
          :label="t('general_email')"
          v-model="contactForm.contactFromEmail"
          name="email"
          type="email"
          autocomplete="email"
          :placeholder="t('general_email_help_required')"
          container-class="mb-0"
          required
        />

        <!-- Message -->
        <app-textarea
          :label="t('general_question')"
          :placeholder="t('general_question_placeholder')"
          name="message"
          v-model="contactForm.contactMessage"
          :rows="5"
          container-class="mb-0"
          required
        />

        <!-- Captcha — challenge image + refresh on one row, full-width answer
           input below. Everything is wrapped in ONE element so AppField does
           not auto-grid its slot children into cramped columns. -->
        <app-field v-slot="{ describedBy }" label-for="contact-captcha" :label="t('forms.captcha_label')" :help="t('general_captcha_help')" container-class="mb-0">
          <div class="flex flex-col gap-2">
            <div class="flex items-center gap-3">
              <div
                class="flex h-12 min-w-[8rem] items-center justify-center rounded-theme-md border border-theme-border bg-theme-bg-subtle px-4"
              >
                <img
                  v-if="captchaData !== ''"
                  :src="`data:image/png;base64,${captchaData}`"
                  :alt="t('forms.captcha_label')"
                  class="h-7"
                />
                <Icon
                  v-else-if="isCaptchaLoading"
                  :path="mdiLoading"
                  size="18"
                  class="animate-spin text-theme-text-muted"
                />
                <span v-else class="text-xs text-theme-text-muted">…</span>
              </div>
              <AppIconButton
                @click="getCaptcha"
                variant="soft"
                tone="primary"
                size="lg"
                :label="t('general_reload')"
                :disabled="isSubmitting"
                :loading="isCaptchaLoading"
                :aria-label="t('general_reload')"
              >
                <Icon :path="mdiReload" size="20" />
              </AppIconButton>
            </div>
            <app-input
              v-model="contactForm.captchaResult"
              name="captcha"
              id="contact-captcha"
              :described-by="describedBy"
              :placeholder="t('forms.captcha_label')"
              required
              container-class="mb-0"
            />
            <input type="hidden" name="captchaKey" v-model="contactForm.captchaKey" />
          </div>
        </app-field>

        <template #afterActions>
          <p class="text-center text-xs text-theme-text-secondary">
            {{ t('contact_response_time') }}
          </p>
        </template>
      </public-form>
    </template>
  </auth-split-layout>

  <!-- Authenticated Contact Page (with sidebar) -->
  <main-section v-else class-name="flex-1">
    <div class="max-w-2xl mx-auto">
      <!-- Page Title -->
      <div class="text-center mb-8">
        <h1 class="text-3xl font-bold text-theme-text dark:text-theme-text">
          {{ t('menu_contact') }}
        </h1>
        <p class="mt-2 text-sm text-theme-text-secondary">
          {{ t('general_question_help') }}
        </p>
      </div>

      <!-- Contact Info Cards -->
      <div class="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-8">
        <a
          href="mailto:info@profiteia.io"
          class="flex items-center gap-3 p-4 rounded-theme-md border border-theme-border dark:border-theme-border bg-theme-surface dark:bg-theme-surface hover:border-theme-palette-emerald/40 dark:hover:border-theme-palette-emerald transition-colors"
        >
          <icon :path="mdiEmailOutline" class="h-5 w-5 text-theme-palette-emerald dark:text-theme-palette-emerald" />
          <span class="text-sm text-theme-text-secondary">info@profiteia.io</span>
        </a>
        <a
          href="tel:+31854884348"
          class="flex items-center gap-3 p-4 rounded-theme-md border border-theme-border dark:border-theme-border bg-theme-surface dark:bg-theme-surface hover:border-theme-palette-emerald/40 dark:hover:border-theme-palette-emerald transition-colors"
        >
          <icon :path="mdiPhone" class="h-5 w-5 text-theme-palette-emerald dark:text-theme-palette-emerald" />
          <span class="text-sm text-theme-text-secondary">085-4884348</span>
        </a>
        <div
          class="flex items-center gap-3 p-4 rounded-theme-md border border-theme-border dark:border-theme-border bg-theme-surface dark:bg-theme-surface"
        >
          <icon
            :path="mdiMapMarkerOutline"
            class="h-5 w-5 text-theme-palette-emerald dark:text-theme-palette-emerald"
          />
          <span class="text-sm text-theme-text-secondary"
            >Spoorlaan 350, 5038 CC, Tilburg</span
          >
        </div>
      </div>

      <!-- Contact Form Card -->
      <form-card
        @submit.prevent="submitContactForm"
        form
      >
        <!-- Alerts -->
        <app-alert
          v-if="successMessage"
          tone="success"
          :icon="mdiAlertCircle"
          variant="outline"
        >
          {{ successMessage }}
        </app-alert>
        <app-alert
          v-if="errorMessage"
          tone="danger"
          :icon="mdiAlertCircle"
          variant="outline"
        >
          {{ errorMessage }}
        </app-alert>

        <!-- Subject -->
        <app-input
          :label="t('general_subject')"
          v-model="contactForm.contactSubject"
          name="subject"
          :placeholder="t('general_subject')"
          required
        />

        <!-- Name -->
        <app-input
          :label="t('general_name')"
          v-model="contactForm.contactFromName"
          name="name"
          autocomplete="name"
          :placeholder="t('general_name_help_required')"
          required
        />

        <!-- Email -->
        <app-input
          :label="t('general_email')"
          v-model="contactForm.contactFromEmail"
          type="email"
          name="email"
          autocomplete="email"
          :placeholder="t('general_email_help_required')"
          required
        />

        <!-- Message -->
        <app-textarea
          :label="t('general_message')"
          v-model="contactForm.contactMessage"
          name="message"
          :placeholder="t('general_message_help')"
          required
        />

        <!-- Captcha -->
        <app-field v-slot="{ describedBy }" label-for="contact-captcha" :label="t('forms.captcha_label')" :help="t('general_captcha_help')">
          <div class="flex items-center gap-3">
            <div
              class="flex h-12 items-center justify-center rounded-theme-md border border-theme-border dark:border-theme-border bg-theme-bg-subtle dark:bg-theme-surface-raised px-3"
            >
              <img
                v-if="captchaData !== ''"
                :src="`data:image/png;base64,${captchaData}`"
                :alt="t('forms.captcha_label')"
                class="h-8"
              />
              <Icon
                v-else-if="isCaptchaLoading"
                :path="mdiLoading"
                size="18"
                class="animate-spin text-theme-text-muted"
              />
            </div>
            <div class="relative flex-1">
              <app-input
                v-model="contactForm.captchaResult"
                name="captcha"
              id="contact-captcha"
              :described-by="describedBy"
                :placeholder="t('forms.captcha_label')"
                required
                container-class="mb-0"
              />
            </div>
            <AppIconButton
              @click="getCaptcha"
              variant="ghost"
              tone="primary"
              size="sm"
              :label="t('general_reload')"
              :disabled="isSubmitting"
              :loading="isCaptchaLoading"
              :aria-label="t('general_reload')"
            >
              <Icon :path="mdiReload" size="20" />
            </AppIconButton>
          </div>
          <input type="hidden" name="captchaKey" v-model="contactForm.captchaKey" />
        </app-field>

        <!-- Submit Button -->
        <app-button
          type="submit"
          :label="t('general_send')"
          color="info"
          :loading="isSubmitting"
          :disabled="isSubmitDisabled"
          class="w-full"
        />

        <p class="mt-3 text-center text-xs text-theme-text-secondary">
          {{ t('contact_response_time') }}
        </p>
      </form-card>
    </div>
  </main-section>
</template>

<script setup>
  import { useNotification } from '@/composables/useNotification';
import {
  mdiAlertCircle,
  mdiArrowRight,
  mdiCheckCircle,
  mdiEmailOutline,
  mdiLoading,
  mdiMapMarkerOutline,
  mdiPhone,
  mdiReload,
  mdiSendOutline,
} from '@mdi/js';
import { computed, onMounted, reactive, ref } from 'vue';
import { useI18n } from 'vue-i18n';

  import Icon from '@/components/Icon';
import AppButton from '@/components/ui/buttons/AppButton';
import AppIconButton from '@/components/ui/buttons/AppIconButton.vue';
import AppAlert from '@/components/ui/feedback/AppAlert.vue';
import AppField from '@/components/ui/forms/AppField.vue';
import AppInput from '@/components/ui/forms/AppInput.vue';
import AppTextarea from '@/components/ui/forms/AppTextarea.vue';
import AuthBrandPanel from '@/components/ui/layout/AuthBrandPanel.vue';
import AuthFormHeader from '@/components/ui/layout/AuthFormHeader.vue';
import AuthSplitLayout from '@/components/ui/layout/AuthSplitLayout.vue';
import FormCard from '@/components/ui/layout/FormCard.vue';
import MainSection from '@/components/ui/layout/MainSection.vue';
import PublicForm from '@/components/ui/layout/PublicForm.vue';
import EmailRepository from '@/repositories/EmailRepository';
import { useAuthStore } from '@/stores/useAuthStore';
import { extractAuthErrorMessage } from '@/utils/authErrorHandler';

  const { t } = useI18n();
  const authStore = useAuthStore();
  const { notifySuccess, notifyError } = useNotification();

  // Check if user is authenticated
  const isAuthenticated = computed(() => authStore.isAuthenticated);

  // Contact methods surfaced on the dark brand panel of the split layout.
  const brandContactFeatures = [
    { icon: mdiEmailOutline, title: 'info@profiteia.io', description: t('general_email') },
    { icon: mdiPhone, title: '085-4884348', description: t('menu_contact') },
    { icon: mdiMapMarkerOutline, title: 'Spoorlaan 350, 5038 CC, Tilburg', description: 'Tilburg' },
  ];

  const captchaData = ref('');
  const isCaptchaLoading = ref(false);
  const isSubmitting = ref(false);
  const errorMessage = ref('');
  const successMessage = ref('');
  // Drives the premium "message sent" state on the public page (hides the form).
  const submitted = ref(false);

  const contactForm = reactive({
    captchaKey: null,
    captchaResult: null,
    contactFromName: null,
    contactFromEmail: null,
    contactSubject: null,
    contactMessage: null,
  });

  const isSubmitDisabled = computed(
    () => isSubmitting.value || isCaptchaLoading.value || !contactForm.captchaKey
  );

  const getCaptcha = () => {
    isCaptchaLoading.value = true;
    contactForm.captchaResult = null;

    EmailRepository.getCaptcha()
      .then((response) => {
        captchaData.value = response.data.captcha_data;
        contactForm.captchaKey = response.data.key;
      })
      .catch(() => {
        captchaData.value = '';
        contactForm.captchaKey = null;
        errorMessage.value = 'Captcha laden mislukt. Probeer het opnieuw.';
      })
      .finally(() => (isCaptchaLoading.value = false));
  };

  const resetForm = () => {
    contactForm.captchaKey = null;
    contactForm.captchaResult = null;
    contactForm.contactFromName = null;
    contactForm.contactFromEmail = null;
    contactForm.contactSubject = null;
    contactForm.contactMessage = null;
  };

  const submitContactForm = () => {
    if (isSubmitDisabled.value) {
      return;
    }

    errorMessage.value = '';
    successMessage.value = '';
    isSubmitting.value = true;

    EmailRepository.submitContactForm(contactForm)
      .then(() => {
        successMessage.value = t('contact_success_title');
        notifySuccess(successMessage.value);
        resetForm();
        submitted.value = true;
      })
      .catch((error) => {
        const message = extractAuthErrorMessage(error, t);
        errorMessage.value = message;
        notifyError(message);
        getCaptcha();
      })
      .finally(() => (isSubmitting.value = false));
  };

  onMounted(getCaptcha);
</script>

<style scoped>
  .fade-enter-active,
  .fade-leave-active {
    transition: opacity 0.2s ease;
  }
  .fade-enter-from,
  .fade-leave-to {
    opacity: 0;
  }
</style>
